import { useInfiniteGetMyAccountDocumentTemplates } from '@/api/queries/me/useGetMyAccountDocumentTemplates';
import { useMyAccountHasFeature } from '@/api/queries/me/useGetMyAccountFeatures';
import projectKeys from '@/api/queries/project/keyFactory';
import { TFolder } from '@/api/queries/project/useGetProjectFolders';
import { PermissionGuard } from '@/ui/components/permissionGuard/PermissionGuard';
import ChooseDocumentTemplateModal from '@/ui/pages/projectPage/components/FilesPanel/components/ChooseDocumentTemplateModal';
import {
  AnchorButton,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  ContextMenuDropdown,
  InputGroup,
  Tooltip,
} from '@payaca/ambrosia';
import { Droppable } from '@payaca/dnd';

import { DocumentInstancePermissions } from '@payaca/permissions/documentInstance/documentInstance.permissions';
import UntitledIcon from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import { useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { FC, useState, type JSX } from 'react';
import { Link, useHistory } from 'react-router-dom';

type Props = {
  isTreeViewOpen: boolean;
  breadcrumbItems: BreadcrumbItem[];
  lastUpdatedFolderEntriesData: number;
  isRefetchingFolderEntries: boolean;
  projectId: string;
  workingFolder?: TFolder;
  onAddFile: () => void;
  onToggleTreeView: () => void;
};
const FilesPanelHeader: FC<Props> = (props) => {
  const {
    isTreeViewOpen,
    breadcrumbItems,
    lastUpdatedFolderEntriesData,
    isRefetchingFolderEntries,
    projectId,
    workingFolder,
    onAddFile,
    onToggleTreeView,
  } = props;

  /**
   * state
   */
  const history = useHistory();
  const [
    isChooseDocumentTemplateModalOpen,
    setIsChooseDocumentTemplateModalOpen,
  ] = useState(false);

  /**
   * Queries
   */
  const queryClient = useQueryClient();
  const { totalCount } = useInfiniteGetMyAccountDocumentTemplates();
  const hasLegacyFormTemplates = useMyAccountHasFeature(
    'hasLegacyFormTemplates'
  );

  const hasDocumentTemplates = totalCount > 0;

  let templateActions: JSX.Element | null = null;
  if (hasDocumentTemplates) {
    templateActions = (
      <InputGroup className="w-max shadow-none" sizeVariant="sm">
        <PermissionGuard
          renderIfHasPermissions={[
            DocumentInstancePermissions.CREATE_DOCUMENT_INSTANCE,
          ]}
        >
          <InputGroup.Button
            onClick={() => setIsChooseDocumentTemplateModalOpen(true)}
            variant="secondary"
          >
            Generate document
          </InputGroup.Button>
        </PermissionGuard>
        {hasLegacyFormTemplates && (
          <ContextMenuDropdown
            as={InputGroup.Button}
            menuItems={[
              {
                label: 'Generate legacy form',
                onClick: () => {
                  history.push(
                    `/forms?projectId=${projectId}&parentFolderId=${
                      workingFolder?.id ?? ''
                    }`
                  );
                },
              },
            ]}
            variant="secondary"
          >
            <UntitledIcon className="w-5 h-5" name="dots-vertical.3" />
          </ContextMenuDropdown>
        )}
      </InputGroup>
    );
  } else if (hasLegacyFormTemplates) {
    templateActions = (
      <AnchorButton
        as={Link}
        className="md:whitespace-nowrap"
        to={`/forms?projectId=${projectId}&parentFolderId=${
          workingFolder?.id ?? ''
        }`}
        variant="secondary"
        size="sm"
      >
        Choose template
      </AnchorButton>
    );
  }

  return (
    <>
      <div className="flex justify-between gap-4">
        <Droppable id="files-tree" disabled={isTreeViewOpen}>
          <Button
            className="aspect-square data-[over]:ring-2 data-[over]:ring-blue-500"
            size="sm"
            variant="secondary"
            onClick={onToggleTreeView}
          >
            <UntitledIcon className="w-4 h-4" name="layout-left.3" />
          </Button>
        </Droppable>

        <Breadcrumb
          className="grow"
          items={breadcrumbItems}
          itemsAreDroppable
          LinkComponent={Link}
        />

        <div className="flex gap-2">
          <Tooltip
            tooltipContent={`Last refreshed: ${formatDistanceToNow(
              lastUpdatedFolderEntriesData,
              { includeSeconds: true }
            )} ago`}
          >
            <Button
              className="h-full"
              variant="secondary"
              onClick={() => {
                void queryClient.invalidateQueries({
                  queryKey: projectKeys.folders(projectId),
                });
              }}
            >
              <UntitledIcon
                className={clstx(
                  'w-4 h-4',
                  isRefetchingFolderEntries && 'animate-spin'
                )}
                name="refresh-cw-01"
              />
            </Button>
          </Tooltip>

          {templateActions}

          <Button size="sm" onClick={onAddFile}>
            Add file
          </Button>
        </div>
      </div>
      <ChooseDocumentTemplateModal
        projectId={projectId}
        folderId={workingFolder?.id}
        isOpen={isChooseDocumentTemplateModalOpen}
        onClose={() => setIsChooseDocumentTemplateModalOpen(false)}
      />
    </>
  );
};

export default FilesPanelHeader;
